import { AxiosInstance } from 'axios';
import {
  BlogPost,
  BlogPostSearchDirectory,
  BlogPostStatus,
  BlogSectionType,
} from 'referral-types';
import { addFormValues, simulate } from './utils';
import { SITE_URL } from './service';
import { type Crop } from 'react-image-crop';

export const baseEndpoint = 'blog-post';

export class BlogPostApi {
  private apiInstance: AxiosInstance;

  constructor(apiInstance: AxiosInstance) {
    this.apiInstance = apiInstance;
  }

  async getBlogPost(id: string): Promise<BlogPost> {
    await simulate();
    const response = await this.apiInstance.get(`/${id}`);

    return response.data;
  }

  async getBlogPosts(): Promise<Array<BlogPost>> {
    await simulate();
    const response = await this.apiInstance.get(`/`);

    return response.data;
  }

  async getFeaturedBlogPosts(): Promise<Array<BlogPost>> {
    await simulate();
    const response = await this.apiInstance.get(`/featured`);

    return response.data;
  }

  async searchPosts(
    searchDirectory: BlogPostSearchDirectory,
  ): Promise<Array<BlogPost>> {
    await simulate();
    const response = await this.apiInstance.post(`/search`, searchDirectory);

    return response.data;
  }

  async createBlogPost(formValues: {
    title: string;
    slug: string;
    excerpt?: string;
    status?: BlogPostStatus;
    isFeatured?: boolean;
    primaryColorHex?: string | null;
    secondaryColorHex?: string | null;
    tertiaryColorHex?: string | null;
    content?: string;
    file?: File;
    squareCrop?: Crop;
    rectangleCrop?: Crop;
  }): Promise<{ id: string }> {
    await simulate();
    const formData = new FormData();
    addFormValues(formValues as Record<string, unknown>, formData);
    const response = await this.apiInstance.post(`/`, formData);

    return response.data;
  }

  async updateBlogPost(
    blogPostId: string,
    formValues: {
      title?: string;
      slug?: string;
      excerpt?: string;
      status?: BlogPostStatus;
      isFeatured?: boolean;
      primaryColorHex?: string | null;
      secondaryColorHex?: string | null;
      tertiaryColorHex?: string | null;
      content?: string;
      file?: File;
      squareCrop?: Crop;
      rectangleCrop?: Crop;
    }
  ): Promise<{ id: string }> {
    await simulate();
    const formData = new FormData();
    addFormValues(formValues as Record<string, unknown>, formData);
    const response = await this.apiInstance.put(`/${blogPostId}`, formData);

    return response.data;
  }

  async createBlogSection(
    blogPostId: string,
    formValues: { type: BlogSectionType; sortOrder?: number }
  ): Promise<{ id: string }> {
    await simulate();
    const response = await this.apiInstance.post(`/${blogPostId}/sections`, formValues);

    return response.data;
  }

  async updateBlogSection(
    blogSectionId: string,
    formValues: { type?: BlogSectionType; sortOrder?: number }
  ): Promise<{ id: string }> {
    await simulate();
    const response = await this.apiInstance.put(`/sections/${blogSectionId}`, formValues);

    return response.data;
  }

  async createBlogSectionRevision(
    blogSectionId: string,
    contentJson: Record<string, unknown>,
    options?: {
      file?: File;
      squareCrop?: Crop;
      rectangleCrop?: Crop;
    }
  ): Promise<{ id: string }> {
    await simulate();

    if (options?.file) {
      const formData = new FormData();
      addFormValues(
        {
          contentJson,
          file: options.file,
          squareCrop: options.squareCrop,
          rectangleCrop: options.rectangleCrop,
        } as Record<string, unknown>,
        formData,
      );

      const response = await this.apiInstance.post(
        `/sections/${blogSectionId}/revisions`,
        formData,
      );

      return response.data;
    }

    const response = await this.apiInstance.post(`/sections/${blogSectionId}/revisions`, { contentJson });

    return response.data;
  }

  async updateBlogSectionContentJson(
    blogSectionId: string,
    contentJson: Record<string, unknown>
  ): Promise<{ id: string }> {
    await simulate();
    const response = await this.apiInstance.put(`/sections/${blogSectionId}/content-json`, { contentJson });

    return response.data;
  }

  async publishBlogPost(blogPostId: string): Promise<{ id: string; status: BlogPostStatus }> {
    await simulate();
    const response = await this.apiInstance.post(`/${blogPostId}/actions/publish`);

    return response.data;
  }

  async unpublishBlogPost(blogPostId: string): Promise<{ id: string; status: BlogPostStatus }> {
    await simulate();
    const response = await this.apiInstance.post(`/${blogPostId}/actions/unpublish`);

    return response.data;
  }

  async archiveBlogPost(blogPostId: string): Promise<{ id: string; status: BlogPostStatus }> {
    await simulate();
    const response = await this.apiInstance.post(`/${blogPostId}/actions/archive`);

    return response.data;
  }

  async deleteBlogPost(blogPostId: string): Promise<{ id: string; deleted: boolean }> {
    await simulate();
    const response = await this.apiInstance.delete(`/${blogPostId}`);

    return response.data;
  }

  async importBlogPost(payload: {
    post: {
      title: string;
      slug: string;
      excerpt?: string;
      status?: BlogPostStatus;
      isFeatured?: boolean;
      tags?: string[];
    };
    sections: Array<{
      type: BlogSectionType;
      sortOrder: number;
      contentJson: Record<string, unknown>;
    }>;
    file?: File;
    squareCrop?: Crop;
    rectangleCrop?: Crop;
    graphicUploads?: Array<{
      sectionIndex: number;
      file: File;
      squareCrop: Crop;
      rectangleCrop: Crop;
    }>;
  }): Promise<{ id: string; slug: string }> {
    await simulate();
    const { file, squareCrop, rectangleCrop, graphicUploads, ...rest } = payload;

    const graphicUploadMeta = (graphicUploads ?? []).map((upload) => ({
      sectionIndex: upload.sectionIndex,
      fileFieldName: `graphicFile_${upload.sectionIndex}`,
      squareCrop: upload.squareCrop,
      rectangleCrop: upload.rectangleCrop,
    }));

    const formData = new FormData();
    formData.append('data', JSON.stringify({ ...rest, graphicUploads: graphicUploadMeta }));
    if (file) {
      formData.append('file', file);
    }
    if (squareCrop) {
      formData.append('squareCrop', JSON.stringify(squareCrop));
    }
    if (rectangleCrop) {
      formData.append('rectangleCrop', JSON.stringify(rectangleCrop));
    }

    (graphicUploads ?? []).forEach((upload) => {
      formData.append(`graphicFile_${upload.sectionIndex}`, upload.file);
    });

    const response = await this.apiInstance.post(`/import`, formData);
    return response.data;
  }

  async getNextBlogPost(excludedSlugs: string[]): Promise<BlogPost | null> {
    await simulate();
    const response = await this.apiInstance.post(`/next`, { excludedSlugs });

    return response.data;
  }

  getBlogPostImageSrc(blogPostId: string): string {
    return `${SITE_URL}/image/blog-post/${blogPostId}`;
  }

  getBlogPostGraphicImageSrc(blogPostId: string, sectionRevisionId: string): string {
    return `${SITE_URL}/image/blog-post/${blogPostId}/graphic/${sectionRevisionId}`;
  }
}
